import { Check, ClipboardList, ShieldCheck, Sparkles, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { useCapability } from '../../hooks/use-capability';
import { api, ApiError } from '../../lib/api-client';
import { useToastStore } from '../../stores/toast-store';

import {
  COLETA_FIELDS,
  coletaErrorMessage,
  fieldsFromDraft,
  fieldsToPayload,
  inheritedDefaults,
  isFinalizable,
  isRouteErrorCode,
  lastSavedLabel,
  progressOf,
  routeErrorMessage,
  schemaToFieldDefs,
  sectionsOf,
  statusLabel,
  type ColetaFieldDef,
} from './coleta-adapter';
import { ColetaDocuments } from './ColetaDocuments';
import { ColetaLegalAdvisory } from './ColetaLegalAdvisory';
import { ColetaError } from './types';
import { useColetaMutations, useColetaSchema } from './use-coleta';

import type { ColetaRouteError, StructuredCollection } from './types';
import type { PersonDetail } from '../persons/types';

const AUTOSAVE_DEBOUNCE_MS = 1200;

export function ColetaPanel({
  conversationId,
  draft,
  bondId,
  personName,
  person,
  cadastralChangeDetected = false,
  onClose,
  onRefetch,
}: {
  conversationId: string;
  draft: StructuredCollection;
  bondId: string | undefined;
  personName: string | null;
  person: PersonDetail | undefined;
  /** Item 363 F3 — detector sinalizou pedido cadastral em canal não-validado. */
  cadastralChangeDetected?: boolean;
  onClose: () => void;
  onRefetch: () => void;
}): JSX.Element {
  const mutations = useColetaMutations(conversationId);
  const pushToast = useToastStore((s) => s.push);

  const editable = draft.status === 'in_progress';

  // PR B (ADR-155 D9) — o form DERIVA do Playbook da conversa (mesma máquina
  // do dispatcher do finalize). Sem dado do schema (carregando OU erro) →
  // form legado FIXO imediatamente: 100% dos tenants sem declaração continuam
  // com render síncrono (D8, sem esperar a rede — Important da revisão
  // adversarial); o swap legado→dinâmico só acontece no caso raro de playbook
  // declarado, e o dispatcher server-side continua autoritativo no finalize.
  const { schema } = useColetaSchema(draft.id);
  const defs: readonly ColetaFieldDef[] =
    schema !== undefined ? schemaToFieldDefs(schema) : COLETA_FIELDS;

  const [form, setForm] = useState<Record<string, string>>(() => fieldsFromDraft(draft.fields));
  // Snapshot vivo do form p/ handlers async (onSuggest decide "campo vazio?"
  // contra o estado pós-await, não contra a closure do clique).
  const formRef = useRef(form);
  formRef.current = form;
  const [docIds, setDocIds] = useState<string[]>(draft.documentIds);
  const dirtyRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Reseed do form quando o draft é recarregado do servidor (409 → onRefetch).
  const [reseedToken, setReseedToken] = useState(0);

  // Item 361 F1 — "Sugerir da conversa": marca de sugestão por campo
  // (key → trecho citado da mensagem do cliente). Editar o campo limpa a marca.
  const canSuggest = useCapability('ai-collection-extraction');
  const [suggesting, setSuggesting] = useState(false);
  const [suggested, setSuggested] = useState<Record<string, string>>({});

  // PR C (ADR-155 D5) — herdar+confirmar: marca de campo pré-preenchido com o
  // default do último episódio concluído ("da última vez — confirme").
  // Editar o campo limpa a marca (mesmo gesto do `suggested`). Guarda o
  // VALOR herdado (não só `true`) — a reconciliação (Important da revisão
  // adversarial, abaixo) precisa comparar o valor antigo com o novo quando o
  // schema muda.
  const [inheritedMarks, setInheritedMarks] = useState<Record<string, string>>({});
  // Snapshot vivo (mesmo padrão de `formRef`) — a reconciliação abaixo lê o
  // valor SEM precisar declarar `inheritedMarks` nas deps do effect (evita
  // reentrância: o próprio effect atualiza `inheritedMarks`).
  const inheritedMarksRef = useRef(inheritedMarks);
  inheritedMarksRef.current = inheritedMarks;

  // ADR-155 Fase 4 (§3.5) — affordance EXPLÍCITA de confirmação do herdado:
  // estado FE irmão do `inheritedMarks` (ZERO wire novo — o servidor CONTINUA
  // derivando `confirmedAt` por igualdade no finalize, PR C §4). Ao tocar
  // "✓ Confirmei com o cliente" a tag muda visualmente; editar o campo limpa
  // os dois estados (vira coleta fresca, mesmo gesto de `inheritedMarks`).
  const [confirmedMarks, setConfirmedMarks] = useState<Record<string, true>>({});

  // Item 363 F1 — congelamento cadastral do bond (regra de ouro 3): o 422 do
  // finalize vira aviso persistente; quem tem a cap supervisora vê o botão
  // "Descongelar vínculo" (o congelamento é gatilho de proteção — Team+).
  const canUnfreeze = useCapability('override-gatilho-protecao');
  const [frozenReason, setFrozenReason] = useState<string | null>(null);
  const [unfreezing, setUnfreezing] = useState(false);
  const frozenBondId = draft.bondId ?? bondId ?? null;

  // PR B — 422 de ROTEAMENTO do dispatcher (collection_target_*): aviso
  // persistente por-campo (não toast que some); editar um campo limpa.
  const [routeErrors, setRouteErrors] = useState<ColetaRouteError[] | null>(null);

  useEffect(() => {
    const reset = fieldsFromDraft(draft.fields);
    // Sincroniza o snapshot vivo ANTES do effect de seed (que roda no MESMO
    // flush, logo abaixo): a checagem "campo vazio?" do re-seed pós-409 deve
    // ler o form RESETADO, não a closure/snapshot pré-reset.
    formRef.current = reset;
    setForm(reset);
    setDocIds(draft.documentIds);
    setSuggested({});
    setInheritedMarks({});
    setConfirmedMarks({});
    inheritedSeededRef.current = false;
    setFrozenReason(null);
    setRouteErrors(null);
    dirtyRef.current = false;
  }, [reseedToken, draft.id]);

  // PR C (ADR-155 D5) — herdar+confirmar: pré-preenche campos episodic VAZIOS
  // com o default do último episódio (marca "da última vez — confirme");
  // dirtyRef dispara o autosave normal (o valor precisa estar no draft p/ o
  // finalize despachar). NUNCA sobrescreve o que o operador já digitou.
  // `reseedToken` nas deps: após 409 o schema/editable ficam estáveis — sem o
  // token, resetar o ref no reseed nunca re-dispararia este effect.
  const inheritedSeededRef = useRef(false);
  useEffect(() => {
    if (inheritedSeededRef.current || !editable || schema === undefined) return;
    inheritedSeededRef.current = true;
    const defaults = inheritedDefaults(schema);
    const empty = Object.entries(defaults).filter(
      ([k]) => (formRef.current[k] ?? '').trim() === '',
    );
    if (empty.length === 0) return;
    dirtyRef.current = true;
    setForm((prev) => ({ ...prev, ...Object.fromEntries(empty) }));
    setInheritedMarks(Object.fromEntries(empty));
  }, [schema, editable, reseedToken]);

  // Important (revisão adversarial PR C, B1) — RECONCILIAÇÃO: a herança pode
  // MUDAR no meio da conversa (a IA remarca/booka noutro episódio → o schema
  // refaz fetch, `staleTime` deixou de ser Infinity — fix irmão em
  // `use-coleta.ts`). Só toca campos AINDA MARCADOS — o operador nunca editou
  // esse campo (por construção: `onField` limpa a marca no primeiro toque).
  // Campo cuja herança SUMIU → limpa (o pré-preenchido não é mais válido);
  // campo cuja herança MUDOU de valor → re-seed com o novo (marca permanece).
  useEffect(() => {
    if (!editable || schema === undefined) return;
    const currentDefaults = inheritedDefaults(schema);
    const marks = inheritedMarksRef.current;
    const markedKeys = Object.keys(marks);
    if (markedKeys.length === 0) return;
    const nextMarks = { ...marks };
    const patch: Record<string, string> = {};
    // ADR-155 Fase 4 (§3.5) — a herança mudou ou sumiu p/ este campo: a
    // confirmação explícita (se houver) valia para o valor ANTERIOR e deixa
    // de ser válida — some junto (o operador reconfirma o novo valor).
    const unconfirmKeys: string[] = [];
    let changed = false;
    for (const key of markedKeys) {
      const newValue = currentDefaults[key];
      if (newValue === undefined) {
        delete nextMarks[key];
        patch[key] = '';
        changed = true;
        unconfirmKeys.push(key);
      } else if (newValue !== marks[key]) {
        nextMarks[key] = newValue;
        patch[key] = newValue;
        changed = true;
        unconfirmKeys.push(key);
      }
    }
    if (!changed) return;
    dirtyRef.current = true;
    setForm((prev) => ({ ...prev, ...patch }));
    setInheritedMarks(nextMarks);
    if (unconfirmKeys.length > 0) {
      setConfirmedMarks((prev) => {
        let removedAny = false;
        const next = { ...prev };
        for (const key of unconfirmKeys) {
          if (key in next) {
            delete next[key];
            removedAny = true;
          }
        }
        return removedAny ? next : prev;
      });
    }
  }, [schema, editable]);

  const handleError = (err: unknown): void => {
    if (err instanceof ColetaError && err.code === 'bond_cadastral_frozen') {
      // Sinal de segurança — não morre em toast: vira aviso persistente.
      setFrozenReason(err.reason);
    } else if (err instanceof ColetaError && isRouteErrorCode(err.code)) {
      // ADR-155 Fase 3 — o dispatcher é tudo-ou-nada: nomeia o(s) campo(s)
      // culpado(s) num aviso persistente acionável (o operador corrige e
      // tenta de novo). Body sem field/allErrors degrada para o toast.
      const list =
        err.allErrors ??
        (err.field !== null ? [{ field: err.field, error: err.code, reason: err.reason }] : null);
      if (list !== null && list.length > 0) {
        setRouteErrors(list);
      } else {
        pushToast({ variant: 'error', title: 'Coleta', description: err.reason });
      }
    } else if (err instanceof ColetaError) {
      pushToast({ variant: 'error', title: 'Coleta', description: coletaErrorMessage(err.code) });
    } else if (err instanceof ApiError && err.status === 409) {
      pushToast({
        variant: 'info',
        title: 'Coleta recarregada',
        description:
          'A coleta foi atualizada em outro lugar. Recarregamos os dados — revise e salve de novo.',
      });
      onRefetch();
      setReseedToken((t) => t + 1);
    } else {
      pushToast({
        variant: 'error',
        title: 'Coleta',
        description: 'Não foi possível salvar. Tente novamente.',
      });
    }
  };

  const doSave = async (manual: boolean): Promise<boolean> => {
    if (!editable || mutations.isSaving) return false;
    try {
      await mutations.save({ fields: fieldsToPayload(form), documentIds: docIds });
      dirtyRef.current = false;
      if (manual) {
        pushToast({ variant: 'success', title: 'Coleta', description: 'Rascunho salvo.' });
      }
      return true;
    } catch (err) {
      handleError(err);
      return false;
    }
  };

  // Auto-save debounced — só dispara após edição do usuário (dirtyRef).
  useEffect(() => {
    if (!editable || !dirtyRef.current) return;
    if (timerRef.current !== undefined) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      void doSave(false);
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (timerRef.current !== undefined) clearTimeout(timerRef.current);
    };
  }, [form, docIds]);

  const onField = (key: string, value: string): void => {
    dirtyRef.current = true;
    setForm((prev) => ({ ...prev, [key]: value }));
    // O operador está corrigindo — o aviso de roteamento deixa de valer.
    setRouteErrors(null);
    // O atendente assumiu o campo — a marca "sugerido" deixa de valer.
    setSuggested((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    // O atendente assumiu o campo — a marca "da última vez" deixa de valer.
    setInheritedMarks((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    // ADR-155 Fase 4 (§3.5) — editar o campo também limpa a confirmação
    // explícita (vira coleta fresca — o gesto de confirmação valia p/ o
    // herdado que acabou de deixar de existir).
    setConfirmedMarks((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // ADR-155 Fase 4 (§3.5) — o operador confirmou com o cliente o valor
  // herdado ("✓ Confirmei com o cliente"). Estado local puro — o servidor
  // não é chamado aqui; o `finalize` (PR C §4) deriva `confirmedAt`
  // comparando o valor final com o herdado, por igualdade.
  const onConfirmInherited = (key: string): void => {
    setConfirmedMarks((prev) => ({ ...prev, [key]: true }));
  };

  // Item 361 F1 — extrai candidatos da conversa e preenche APENAS campos
  // vazios (nunca sobrescreve o que o atendente digitou). Auto-save normal
  // persiste; a IA nunca finaliza.
  const onSuggest = async (): Promise<void> => {
    if (!editable || suggesting) return;
    setSuggesting(true);
    try {
      const result = await api.extractCollectionSuggestions(conversationId);
      const applied: Record<string, string> = {};
      const next = { ...formRef.current };
      for (const c of result.candidates) {
        const current = next[c.key] ?? '';
        if (current.trim() === '') {
          next[c.key] = c.value;
          applied[c.key] = c.sourceQuote;
        }
      }
      if (Object.keys(applied).length > 0) {
        dirtyRef.current = true;
        setForm(next);
        setSuggested((prev) => ({ ...prev, ...applied }));
      }
      const appliedCount = Object.keys(applied).length;
      const skippedFilled = result.candidates.length - appliedCount;
      pushToast({
        variant: appliedCount > 0 ? 'success' : 'info',
        title: 'Sugerir da conversa',
        description:
          appliedCount > 0
            ? `${appliedCount} campo(s) sugerido(s) a partir das mensagens do cliente — revise antes de finalizar.` +
              (skippedFilled > 0 ? ` ${skippedFilled} já preenchido(s) foram mantidos.` : '') +
              (result.discardedCount > 0
                ? ` ${result.discardedCount} descartado(s) por falta de evidência na conversa.`
                : '')
            : 'Nenhum dado cadastral novo encontrado nas mensagens do cliente.',
      });
    } catch {
      pushToast({
        variant: 'error',
        title: 'Sugerir da conversa',
        description: 'Não foi possível gerar sugestões agora. Tente novamente.',
      });
    } finally {
      setSuggesting(false);
    }
  };
  const onToggleDoc = (id: string): void => {
    dirtyRef.current = true;
    setDocIds((prev) => (prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]));
  };

  const onFinalize = async (): Promise<void> => {
    // Garante o último estado persistido antes de finalizar (finalize usa o que
    // está no servidor, não o form local).
    if (dirtyRef.current) {
      const ok = await doSave(false);
      if (!ok) return;
    }
    try {
      await mutations.finalize();
      pushToast({
        variant: 'success',
        title: 'Coleta finalizada',
        description: 'Os dados foram aplicados à pessoa e uma pendência de revisão foi criada.',
      });
    } catch (err) {
      handleError(err);
    }
  };

  const onCancel = async (): Promise<void> => {
    try {
      await mutations.cancel();
      pushToast({
        variant: 'info',
        title: 'Coleta cancelada',
        description: 'O rascunho foi descartado.',
      });
      onClose();
    } catch (err) {
      handleError(err);
    }
  };

  // Item 363 F1 — descongela o vínculo após revisão humana (cap supervisora).
  const onUnfreeze = async (): Promise<void> => {
    if (frozenBondId === null || unfreezing) return;
    setUnfreezing(true);
    try {
      await api.unfreezeBondCadastral(frozenBondId);
      setFrozenReason(null);
      pushToast({
        variant: 'success',
        title: 'Vínculo descongelado',
        description: 'As alterações cadastrais deste vínculo foram liberadas após revisão.',
      });
    } catch {
      pushToast({
        variant: 'error',
        title: 'Descongelar vínculo',
        description: 'Não foi possível descongelar o vínculo. Tente novamente.',
      });
    } finally {
      setUnfreezing(false);
    }
  };

  // Item 361 F2 — dispara o fluxo de confirmação com o titular (trava o rascunho).
  const onRequestConfirmation = async (): Promise<void> => {
    // O fluxo confirma o que está no SERVIDOR — persiste o form antes.
    if (dirtyRef.current) {
      const ok = await doSave(false);
      if (!ok) return;
    }
    try {
      await mutations.requestTitularConfirmation();
      pushToast({
        variant: 'success',
        title: 'Confirmação enviada',
        description:
          'O titular vai receber a verificação no canal validado dele. O rascunho fica travado até a resposta (ou 48h).',
      });
    } catch (err) {
      handleError(err);
    }
  };

  const progress = progressOf(form, defs);
  const finalizable = isFinalizable(form, defs);
  const completed = draft.status === 'completed';
  const awaiting = draft.status === 'awaiting_confirmation';
  const cancelledByFraud =
    draft.status === 'cancelled' && draft.confirmationOutcome === 'denied_fraud';
  // ADR-155 Fase 4 (§3.5) — herdados AINDA NÃO confirmados pelo operador.
  const pendingConfirmationCount = Object.keys(inheritedMarks).filter(
    (key) => confirmedMarks[key] === undefined,
  ).length;

  return (
    <div className="coleta-panel" role="dialog" aria-label="Coleta estruturada de dados">
      <div className="coleta-panel__head">
        <ClipboardList size={14} style={{ color: 'var(--brand)' }} />
        <div style={{ flex: 1 }}>
          <div className="coleta-panel__title">Coleta de dados</div>
          <div className="coleta-panel__handle">
            {personName ?? 'sem identidade'} · {statusLabel(draft.status)}
          </div>
        </div>
        <button
          className="btn btn--ghost btn--sm btn--icon"
          type="button"
          aria-label="Fechar"
          onClick={onClose}
        >
          <X size={14} />
        </button>
      </div>

      <div className={`coleta-progress${completed ? ' coleta-progress--success' : ''}`}>
        <div className="coleta-progress__bar" style={{ width: `${progress}%` }} />
      </div>

      <div className="coleta-panel__body">
        {!editable && !awaiting && (
          <div
            className={`coleta-note ${completed ? 'coleta-note--success' : 'coleta-note--danger'}`}
          >
            <Check size={13} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>
              {completed ? (
                <>
                  <strong>Coleta finalizada.</strong> Os campos confirmados foram aplicados à pessoa
                  e uma pendência de revisão foi gerada
                  {draft.generatedPendencyId !== null
                    ? ` (#${draft.generatedPendencyId.slice(0, 8)})`
                    : ''}
                  .
                </>
              ) : cancelledByFraud ? (
                <>
                  <strong>Rascunho descartado por segurança.</strong> O titular respondeu que NÃO
                  reconhece o pedido de alteração. As alterações cadastrais deste vínculo estão
                  congeladas até revisão humana — veja a pendência gerada.
                  {canUnfreeze && frozenBondId !== null && (
                    <button
                      className="btn btn--secondary btn--sm coleta-unfreeze-btn"
                      type="button"
                      onClick={() => void onUnfreeze()}
                      disabled={unfreezing}
                      title="Libera as alterações cadastrais deste vínculo após a revisão humana da pendência de fraude."
                    >
                      {unfreezing ? 'Descongelando…' : 'Descongelar vínculo'}
                    </button>
                  )}
                </>
              ) : (
                <>
                  <strong>Coleta cancelada.</strong> O rascunho foi descartado.
                </>
              )}
            </span>
          </div>
        )}

        {awaiting && (
          <div className="coleta-note coleta-note--info">
            <ShieldCheck size={13} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>
              <strong>Aguardando confirmação do titular.</strong> Enviamos a verificação ao canal
              validado do titular
              {draft.confirmationStage === 'awaiting_step2'
                ? ' — ele confirmou o pedido e agora valida os dados (etapa 2 de 2)'
                : ' (etapa 1 de 2)'}
              . O rascunho fica travado até a resposta ou por 48h.
            </span>
          </div>
        )}

        {editable && draft.confirmationOutcome === 'confirmed' && (
          <div className="coleta-note coleta-note--success">
            <ShieldCheck size={13} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>
              <strong>Titular confirmou os dados</strong>
              {draft.titularConfirmedAt !== null
                ? ` em ${new Date(draft.titularConfirmedAt).toLocaleString('pt-BR')}`
                : ''}
              . Revise e finalize a coleta.
            </span>
          </div>
        )}
        {editable && draft.confirmationOutcome === 'denied_data' && (
          <div className="coleta-note coleta-note--danger">
            <X size={13} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>
              <strong>O titular apontou dados incorretos.</strong> Revise o rascunho com o cliente
              antes de finalizar.
            </span>
          </div>
        )}
        {editable && frozenReason !== null && (
          <div className="coleta-note coleta-note--danger">
            <ShieldCheck size={13} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>
              <strong>Alterações cadastrais congeladas.</strong> {frozenReason} A coleta não pode
              ser finalizada enquanto o vínculo estiver congelado.
              {canUnfreeze && frozenBondId !== null ? (
                <button
                  className="btn btn--secondary btn--sm coleta-unfreeze-btn"
                  type="button"
                  onClick={() => void onUnfreeze()}
                  disabled={unfreezing}
                  title="Libera as alterações cadastrais deste vínculo após a revisão humana da pendência de fraude."
                >
                  {unfreezing ? 'Descongelando…' : 'Descongelar vínculo'}
                </button>
              ) : (
                ' Acione quem revisa pendências de fraude (supervisão) para descongelar.'
              )}
            </span>
          </div>
        )}

        {editable && draft.confirmationOutcome === 'expired' && (
          <div className="coleta-note coleta-note--info">
            <X size={13} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>
              <strong>A confirmação expirou sem resposta (48h).</strong> Nada foi alterado. Você
              pode reenviar a confirmação ou usar a validação documental (anexos).
            </span>
          </div>
        )}

        {/* Item 363 F3 (degrau 1) — o detector sinalizou pedido de alteração
            cadastral vindo de canal NÃO-validado. Mais forte que o hint de
            orientação (que ele substitui para não duplicar a mensagem). */}
        {editable && cadastralChangeDetected && (
          <div className="coleta-note coleta-note--danger coleta-cadastral-detected">
            <ShieldCheck size={13} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>
              <strong>Pedido de alteração cadastral detectado de canal não-validado.</strong>{' '}
              Confirme com o titular antes de finalizar (botão abaixo).
            </span>
          </div>
        )}

        {/* Item 363 F2 (D4) — orientação sobre quem está pedindo a alteração.
            S1 (titular validado) fica em silêncio; o hint só aparece quando o
            pedido NÃO vem do titular. Orientação, não enforcement. */}
        {editable &&
          !cadastralChangeDetected &&
          draft.personId !== null &&
          draft.requesterAuthorization === 'third_party' && (
            <div className="coleta-note coleta-note--info coleta-requester-hint">
              <ShieldCheck size={13} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>
                <strong>Pedido não veio do titular validado.</strong> Recomendamos confirmar com o
                titular antes de finalizar (botão abaixo).
              </span>
            </div>
          )}
        {editable &&
          draft.personId !== null &&
          draft.requesterAuthorization === 'authorized_contact_cadastral' && (
            <div className="coleta-note coleta-note--info coleta-requester-hint">
              <ShieldCheck size={13} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>
                <strong>Contato autorizado para alterações cadastrais.</strong> O titular concedeu
                este escopo — a confirmação com o titular é opcional (cortesia de segurança).
              </span>
            </div>
          )}

        {/* PR B — 422 de roteamento do dispatcher: tudo-ou-nada, campo(s) nomeado(s). */}
        {editable && routeErrors !== null && routeErrors.length > 0 && (
          <div className="coleta-note coleta-note--danger coleta-route-errors">
            <X size={13} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>
              <strong>A coleta não pôde ser finalizada — nada foi gravado.</strong>
              {routeErrors.map((e) => (
                <span key={e.field} style={{ display: 'block', marginTop: 2 }}>
                  {routeErrorMessage(e, defs)}
                </span>
              ))}
            </span>
          </div>
        )}

        {canSuggest && editable && (
          <button
            className="btn btn--secondary btn--sm coleta-suggest-btn"
            type="button"
            onClick={() => void onSuggest()}
            disabled={suggesting}
          >
            <Sparkles size={13} />
            {suggesting ? 'Sugerindo…' : 'Sugerir da conversa'}
          </button>
        )}

        {/* PR B (ADR-155 D9) — seções DERIVADAS dos defs (Playbook ou legado). */}
        {sectionsOf(defs).map((s) => (
          <ColetaSection
            key={s.section}
            label={s.label}
            fields={s.fields}
            form={form}
            suggested={suggested}
            inheritedMarks={inheritedMarks}
            confirmedMarks={confirmedMarks}
            onField={onField}
            onConfirmInherited={onConfirmInherited}
            disabled={!editable}
          />
        ))}

        <ColetaDocuments
          bondId={bondId}
          selectedIds={docIds}
          onToggle={onToggleDoc}
          disabled={!editable}
        />

        {person !== undefined && <ColetaLegalAdvisory person={person} />}
      </div>

      <div className="coleta-panel__foot">
        <span className="muted mono" style={{ fontSize: 10 }}>
          {progress}% preenchido · salvo {lastSavedLabel(draft.updatedAt)}
          {editable && pendingConfirmationCount > 0 && (
            <>
              {' · '}
              <span data-testid="coleta-pending-confirmation-count">
                {pendingConfirmationCount} dado(s) da última vez aguardando confirmação
              </span>
            </>
          )}
        </span>
        <div style={{ flex: 1 }} />
        {editable ? (
          <>
            <button
              className="btn btn--ghost btn--sm"
              type="button"
              onClick={() => void onCancel()}
              disabled={mutations.isCancelling}
            >
              Cancelar
            </button>
            {draft.personId !== null && (
              <button
                className="btn btn--secondary btn--sm coleta-confirm-titular-btn"
                type="button"
                onClick={() => void onRequestConfirmation()}
                disabled={mutations.isRequestingConfirmation}
                title="Envia a verificação em 2 etapas ao canal validado do titular — use quando o pedido de alteração não veio do próprio titular."
              >
                <ShieldCheck size={13} />
                {mutations.isRequestingConfirmation ? 'Enviando…' : 'Confirmar com o titular'}
              </button>
            )}
            <button
              className="btn btn--secondary btn--sm"
              type="button"
              onClick={() => void doSave(true)}
              disabled={mutations.isSaving}
            >
              {mutations.isSaving ? 'Salvando…' : 'Salvar parcial'}
            </button>
            <button
              className="btn btn--primary btn--sm"
              type="button"
              onClick={() => void onFinalize()}
              disabled={!finalizable || mutations.isFinalizing}
              title={finalizable ? undefined : 'Preencha os campos obrigatórios para finalizar.'}
            >
              {mutations.isFinalizing ? 'Finalizando…' : 'Finalizar coleta'}
            </button>
          </>
        ) : awaiting ? (
          <>
            <button
              className="btn btn--ghost btn--sm"
              type="button"
              onClick={() => void onCancel()}
              disabled={mutations.isCancelling}
            >
              Cancelar coleta
            </button>
            <button className="btn btn--primary btn--sm" type="button" onClick={onClose}>
              Voltar à conversa
            </button>
          </>
        ) : (
          <button className="btn btn--primary btn--sm" type="button" onClick={onClose}>
            Voltar à conversa
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Seção de campos ─────────────────────────────────────────────────────

function ColetaSection({
  label,
  fields,
  form,
  suggested,
  inheritedMarks,
  confirmedMarks,
  onField,
  onConfirmInherited,
  disabled,
}: {
  label: string;
  /** PR B — os defs vêm do chamador (Playbook ou legado), não do global. */
  fields: readonly ColetaFieldDef[];
  form: Record<string, string>;
  suggested: Record<string, string>;
  /** PR C (ADR-155 D5) — campos pré-preenchidos com o herdado do último episódio. */
  inheritedMarks: Record<string, string>;
  /** ADR-155 Fase 4 (§3.5) — campos herdados já confirmados pelo operador. */
  confirmedMarks: Record<string, true>;
  onField: (key: string, value: string) => void;
  onConfirmInherited: (key: string) => void;
  disabled: boolean;
}): JSX.Element {
  const rows = groupHalfSpans(fields);
  return (
    <div className="coleta-section">
      <span className="coleta-section__label">{label}</span>
      {rows.map((row) =>
        row.length === 2 ? (
          <div key={row[0]!.key} className="coleta-grid coleta-grid--2">
            {row.map((f) => (
              <Field
                key={f.key}
                def={f}
                value={form[f.key] ?? ''}
                sourceQuote={suggested[f.key]}
                isInherited={inheritedMarks[f.key] !== undefined}
                isConfirmed={confirmedMarks[f.key] === true}
                onChange={onField}
                onConfirmInherited={onConfirmInherited}
                disabled={disabled}
              />
            ))}
          </div>
        ) : (
          <Field
            key={row[0]!.key}
            def={row[0]!}
            value={form[row[0]!.key] ?? ''}
            sourceQuote={suggested[row[0]!.key]}
            isInherited={inheritedMarks[row[0]!.key] !== undefined}
            isConfirmed={confirmedMarks[row[0]!.key] === true}
            onChange={onField}
            onConfirmInherited={onConfirmInherited}
            disabled={disabled}
          />
        ),
      )}
    </div>
  );
}

/** Agrupa campos de meia-largura consecutivos em linhas de 2 colunas. */
function groupHalfSpans(fields: readonly ColetaFieldDef[]): ColetaFieldDef[][] {
  const rows: ColetaFieldDef[][] = [];
  for (let i = 0; i < fields.length; i += 1) {
    const cur = fields[i]!;
    const next = fields[i + 1];
    if (cur.span === 0.5 && next?.span === 0.5) {
      rows.push([cur, next]);
      i += 1;
    } else {
      rows.push([cur]);
    }
  }
  return rows;
}

function Field({
  def,
  value,
  sourceQuote,
  isInherited,
  isConfirmed,
  onChange,
  onConfirmInherited,
  disabled,
}: {
  def: ColetaFieldDef;
  value: string;
  sourceQuote: string | undefined;
  /** PR C (ADR-155 D5) — campo pré-preenchido com o herdado do último episódio. */
  isInherited: boolean;
  /** ADR-155 Fase 4 (§3.5) — operador já tocou "✓ Confirmei com o cliente". */
  isConfirmed: boolean;
  onChange: (key: string, value: string) => void;
  onConfirmInherited: (key: string) => void;
  disabled: boolean;
}): JSX.Element {
  const isSuggested = sourceQuote !== undefined;
  return (
    <div className="coleta-field">
      {/* Nota: o botão "✓ Confirmei" é IRMÃO do <label>, não descendente —
          um <button> dentro de um <label> vira labelable implícito e
          `getByLabelText`/leitor de tela passam a associar DOIS elementos
          ao mesmo texto do label (o input via `htmlFor` E o botão via
          wrapping). `.coleta-field__label-row` mantém o layout inline. */}
      <div className="coleta-field__label-row">
        <label className="coleta-field__label" htmlFor={`coleta-${def.key}`}>
          {def.label}
          {def.required && <span className="coleta-field__label-req">*</span>}
          {def.tag !== undefined && (
            <span
              className="coleta-field__target-tag"
              title="Destino do dado ao finalizar (declarado no Playbook — ADR-155)"
            >
              {def.tag}
            </span>
          )}
          {isSuggested && (
            <span className="coleta-field__suggested-tag">
              <Sparkles size={9} />
              sugerido
            </span>
          )}
          {/* ADR-155 Fase 4 (§3.5) — affordance explícita de confirmação: o
              herdado nasce "da última vez — confirme"; ao confirmar (botão
              irmão abaixo), a tag muda p/ "confirmado com o cliente" (tom
              success). ZERO wire novo — o servidor deriva `confirmedAt` por
              igualdade no finalize (PR C §4). */}
          {isInherited && !isConfirmed && (
            <span
              className="coleta-field__inherited-tag"
              title="Valor herdado do último episódio — confirme com o cliente antes de finalizar."
            >
              da última vez — confirme
            </span>
          )}
          {isInherited && isConfirmed && (
            <span
              className="coleta-field__confirmed-tag"
              title="Você confirmou este valor com o cliente."
            >
              confirmado com o cliente
            </span>
          )}
        </label>
        {isInherited && !isConfirmed && (
          <button
            type="button"
            className="coleta-field__confirm-btn"
            onClick={() => onConfirmInherited(def.key)}
            disabled={disabled}
          >
            ✓ Confirmei com o cliente
          </button>
        )}
      </div>
      <input
        id={`coleta-${def.key}`}
        type="text"
        value={value}
        placeholder={def.placeholder}
        disabled={disabled}
        className={isSuggested ? 'coleta-field__input--suggested' : undefined}
        onChange={(e) => onChange(def.key, e.target.value)}
      />
      {isSuggested && (
        <span className="coleta-field__provenance">Da conversa: &ldquo;{sourceQuote}&rdquo;</span>
      )}
    </div>
  );
}
